import countriesData from '../data/countries.json';
import i18n from '../utils/i18n.js';
import Cart from '../cart/cart.js';
import { formatVolume, normalizeText } from '../utils/utils.js';

class EsimStore {
    constructor(provider) {
        this.provider = provider;
        this.allLocations = [];
        this.countriesFromJson = []; // Countries from countries.json for Spanish language
        this.allPlans = [];
        this.selectedCountries = [];
        this.currentSelectedLocation = null;
        this.cart = new Cart('ily-cart');
    }

    /**
     * Loads all locations from API
     */
    async loadAllLocations() {
        try {
            const response = await this.provider.locations();
            if (response.success && response.obj.locationList) {
                this.allLocations = response.obj.locationList;
            }
        } catch (error) {
            console.error('Error loading locations:', error);
        }
    }

    /**
     * Loads countries from countries.json file
     */
    async loadCountriesFromJson() {
        try {
            this.countriesFromJson = countriesData.filter(country => !country.inactive);
        } catch (error) {
            console.error('Error loading countries.json:', error);
            this.countriesFromJson = [];
        }
    }

    /**
     * Loads all eSIM plans from API with optional filters
     * @param {Object} options - Filter options
     * @param {Array<number>} options.supportTopUpType - Filter by top-up support (default: [1, 2])
     * @param {Array<number>} options.dataType - Filter by data type (default: [1])
     */
    async loadAllPlans(options = {}) {
        try {
            const {
                supportTopUpType = [1, 2],
                dataType = [1]
            } = options;

            this.allPlans = [];
            const allPackagesResponse = await this.provider.packages({});
            if (allPackagesResponse.success && allPackagesResponse.obj?.packageList) {
                let plans = allPackagesResponse.obj.packageList;
                const supportTopUpTypeArray = Array.isArray(supportTopUpType) ? supportTopUpType : [supportTopUpType];
                plans = plans.filter(plan => {
                    const planTopUpType = plan.supportTopUpType;
                    return supportTopUpTypeArray.includes(planTopUpType);
                });
                const dataTypeArray = Array.isArray(dataType) ? dataType : [dataType];
                plans = plans.filter(plan => {
                    const planDataType = plan.dataType;
                    return dataTypeArray.includes(planDataType);
                });
                
                this.allPlans = plans;
            }
        } catch (error) {
            console.error('Error loading plans:', error);
        }
    }

    /**
     * Gets location name by code, prioritizing countries.json for Spanish
     * @param {string} locationCode - ISO-2 location code
     * @returns {string} Location name or code if not found
     */
    getLocationName(locationCode) {
        if (!locationCode) {
            return locationCode || 'Unknown';
        }

        const isSpanish = i18n.getLanguage() === 'es';
        if (isSpanish && this.countriesFromJson.length > 0) {
            const locationCodeLower = locationCode.toLowerCase();
            const country = this.countriesFromJson.find(c => c.alpha2.toLowerCase() === locationCodeLower);
            if (country) {
                return country.name;
            }
        }

        if (this.allLocations.length > 0) {
            const location = this.allLocations.find(loc => loc.code === locationCode);
            if (location) {
                return location.name;
            }
        }

        return locationCode;
    }

    /**
     * Generates flag icon HTML for location code
     * @param {string} locationCode - ISO-2 location code
     * @returns {string} HTML string with flag icon
     */
    getFlagIcon(locationCode) {
        const code = locationCode || 'xx';
        let codeLower = code.toLowerCase();
        if (codeLower.startsWith('eu')) codeLower = 'eu';
        if (codeLower.startsWith('gl')) codeLower = 'un';
        return `<span class="fi fi-${codeLower} rounded-1"></span>`;
    }

    /**
     * Searches locations by name or code
     * @param {string} query - Search query
     * @param {boolean} useCountriesJson - Use countries.json instead of API (default: false)
     * @returns {Array<Object>} Array of matching locations (max 10)
     */
    searchLocations(query, useCountriesJson = false) {
        if (!query || query.length < 1) return [];

        const normalizedQuery = normalizeText(query);
        const queryParts = normalizedQuery.split(' ').filter(part => part.length > 0);

        if (useCountriesJson && this.countriesFromJson.length > 0) {
            return this.countriesFromJson
                .map(country => ({
                    name: country.name,
                    code: country.alpha2.toUpperCase(), // Use alpha2 as code
                    type: 1 // All countries from JSON are type 1
                }))
                .filter(location => {
                    const normalizedName = normalizeText(location.name);
                    const normalizedCode = normalizeText(location.code);

                    if (normalizedCode === normalizedQuery) return true;

                    const nameMatches = queryParts.every(part =>
                        normalizedName.includes(part) || normalizedCode.includes(part)
                    );

                    return nameMatches;
                })
                .sort((a, b) => {
                    const aCodeMatch = normalizeText(a.code) === normalizedQuery;
                    const bCodeMatch = normalizeText(b.code) === normalizedQuery;
                    if (aCodeMatch && !bCodeMatch) return -1;
                    if (!aCodeMatch && bCodeMatch) return 1;

                    const aStartsWith = normalizeText(a.name).startsWith(normalizedQuery);
                    const bStartsWith = normalizeText(b.name).startsWith(normalizedQuery);
                    if (aStartsWith && !bStartsWith) return -1;
                    if (!aStartsWith && bStartsWith) return 1;

                    return a.name.localeCompare(b.name);
                })
                .slice(0, 10);
        }

        return this.allLocations.filter(location => {
            const normalizedName = normalizeText(location.name);
            const normalizedCode = normalizeText(location.code);

            if (normalizedCode === normalizedQuery) return true;

            const nameMatches = queryParts.every(part =>
                normalizedName.includes(part) || normalizedCode.includes(part)
            );

            return nameMatches;
        })
            .sort((a, b) => {
                const aCodeMatch = normalizeText(a.code) === normalizedQuery;
                const bCodeMatch = normalizeText(b.code) === normalizedQuery;
                if (aCodeMatch && !bCodeMatch) return -1;
                if (!aCodeMatch && bCodeMatch) return 1;

                const aStartsWith = normalizeText(a.name).startsWith(normalizedQuery);
                const bStartsWith = normalizeText(b.name).startsWith(normalizedQuery);
                if (aStartsWith && !bStartsWith) return -1;
                if (!aStartsWith && bStartsWith) return 1;

                if (a.type !== b.type) return a.type - b.type;
                return a.name.localeCompare(b.name);
            })
            .slice(0, 10);
    }

    /**
     * Adds country to selected countries list
     * @param {Object} location - Location object with code and name
     * @returns {boolean} True if added, false if already exists
     */
    addCountry(location) {
        if (this.selectedCountries.find(c => c.code === location.code)) {
            return false; // Already added
        }
        this.selectedCountries.push(location);
        return true;
    }

    /**
     * Removes country from selected countries list
     * @param {string} locationCode - Location code to remove
     */
    removeCountry(locationCode) {
        this.selectedCountries = this.selectedCountries.filter(c => c.code !== locationCode);
    }

    /**
     * @returns {Array<Object>} Array of selected countries
     */
    getSelectedCountries() {
        return this.selectedCountries;
    }

    /**
     * Sets current selected location for filtering
     * @param {Object} locationData - Location data with plans
     */
    setCurrentLocation(locationData) {
        this.currentSelectedLocation = locationData;
    }

    /**
     * @returns {Object|null} Current selected location or null
     */
    getCurrentLocation() {
        return this.currentSelectedLocation;
    }

    /**
     * Clears current selected location
     */
    clearCurrentLocation() {
        this.currentSelectedLocation = null;
    }

    /**
     * Filters plans by selected countries, includes regional plans for multiple countries
     * @returns {Object|null} Plans grouped by locationCode or null if no matches
     */
    filterPlans() {
        if (this.selectedCountries.length === 0) {
            return null;
        }

        const countryCodes = this.selectedCountries.map(c => c.code);
        let matchingPlans = this.allPlans.filter(plan => {
            const planLocationCode = plan.locationCode || plan.location;
            return countryCodes.includes(planLocationCode);
        });

        if (this.selectedCountries.length > 1) {
            const regionalPlans = this.allPlans.filter(plan => {
                if (!plan.locationNetworkList || !Array.isArray(plan.locationNetworkList)) {
                    return false;
                }

                const networkListCodes = plan.locationNetworkList.map(loc =>
                    loc.locationCode || loc.code || loc
                );

                return countryCodes.every(code => networkListCodes.includes(code));
            });

            const existingPlanCodes = new Set(matchingPlans.map(p => p.packageCode));
            regionalPlans.forEach(plan => {
                if (!existingPlanCodes.has(plan.packageCode)) {
                    matchingPlans.push(plan);
                }
            });
        }

        if (matchingPlans.length === 0) {
            return null;
        }

        const plansByLocation = {};
        matchingPlans.forEach(plan => {
            const locationCode = plan.locationCode || plan.location || 'UNKNOWN';
            if (!plansByLocation[locationCode]) {
                const locationName = this.getLocationName(locationCode);
                plansByLocation[locationCode] = {
                    locationCode: locationCode,
                    location: locationName,
                    plans: []
                };
            }
            plansByLocation[locationCode].plans.push(plan);
        });

        return plansByLocation;
    }

    /**
     * Adds eSIM package to cart
     * @param {Object} packageData - Package data object
     * @returns {Array} Updated cart array
     */
    addToCart(packageData) {
        return this.cart.addToCart(packageData, 'packageCode');
    }

    /**
     * Removes package from cart by packageCode
     * @param {string} packageCode - Package code to remove
     * @returns {Array} Updated cart array
     */
    removeFromCart(packageCode) {
        return this.cart.removeFromCart(packageCode, 'packageCode');
    }

    /**
     * Updates quantity of package in cart
     * @param {string} packageCode - Package code
     * @param {number} quantity - New quantity
     * @returns {Array} Updated cart array
     */
    updateCartQuantity(packageCode, quantity) {
        return this.cart.updateCartQuantity(packageCode, quantity, 'packageCode');
    }

    /**
     * @returns {Array} Current cart items
     */
    getCart() {
        return this.cart.getCart();
    }

    /**
     * @returns {number} Total cart value
     */
    getCartTotal() {
        return this.cart.getCartTotal('price');
    }

    /**
     * @returns {Array} Empty cart array
     */
    clearCart() {
        return this.cart.clearCart();
    }

    /**
     * Prepares location data for tag rendering
     * @param {Object} location - Location object
     * @returns {Object} Tag data with code, name, and flagIcon
     */
    prepareTagData(location) {
        return {
            code: location.code,
            name: location.name,
            flagIcon: this.getFlagIcon(location.code)
        };
    }

    /**
     * Prepares matching plans data for rendering grouped by location
     * @param {Object} plansByLocation - Plans grouped by locationCode
     * @returns {Array<Object>|null} Array of location plan data or null
     */
    prepareMatchingPlansData(plansByLocation) {
        if (!plansByLocation) return null;

        const locationKeys = Object.keys(plansByLocation);
        if (locationKeys.length === 0) return null;

        return locationKeys.map((locationCode) => {
            const locationData = plansByLocation[locationCode];
            const planCount = locationData.plans.length;
            const locationName = locationData.location || this.getLocationName(locationCode);
            const prices = locationData.plans.map(p => p.price / 10000);
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            const priceRange = minPrice === maxPrice
                ? `$${minPrice.toFixed(2)}`
                : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;

            return {
                locationCode: locationCode,
                locationName: locationName,
                flagIcon: this.getFlagIcon(locationCode),
                planCount: planCount,
                planText: planCount !== 1 ? i18n.t('plans') : i18n.t('plan'),
                priceRange: priceRange,
                locationData: locationData // Keep reference to original data
            };
        });
    }

    /**
     * Prepares duration options data from location plans
     * @param {Object} locationData - Location data with plans array
     * @returns {Array<Object>} Array of duration data with price ranges
     */
    prepareDurationsData(locationData) {
        if (!locationData || !locationData.plans) return [];

        const durationsMap = {};
        locationData.plans.forEach(plan => {
            const durationKey = `${plan.duration} ${plan.durationUnit?.toLowerCase() || 'days'}`;
            if (!durationsMap[durationKey]) {
                durationsMap[durationKey] = {
                    duration: plan.duration,
                    durationUnit: plan.durationUnit,
                    display: durationKey,
                    plans: []
                };
            }
            durationsMap[durationKey].plans.push(plan);
        });

        const durations = Object.values(durationsMap).sort((a, b) => a.duration - b.duration);

        return durations.map(duration => {
            const prices = duration.plans.map(p => p.price / 10000);
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            const priceRange = minPrice === maxPrice
                ? `$${minPrice.toFixed(2)}`
                : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;

            return {
                duration: duration.duration,
                durationUnit: duration.durationUnit,
                display: duration.display,
                priceRange: priceRange,
                packageCount: duration.plans.length,
                packageText: duration.plans.length !== 1 ? i18n.t('packages') : i18n.t('package'),
                plans: duration.plans // Keep reference to original plans
            };
        });
    }

    /**
     * Prepares package data grouped by volume for specific duration
     * @param {number} duration - Duration value
     * @param {string} unit - Duration unit (days, months, etc.)
     * @returns {Array<Object>} Array of package data grouped by volume
     */
    preparePackagesData(duration, unit) {
        const currentLocation = this.getCurrentLocation();
        if (!currentLocation) return [];

        const filteredPlans = currentLocation.plans.filter(plan =>
            plan.duration === duration && plan.durationUnit === unit
        );

        const volumesMap = {};
        filteredPlans.forEach(plan => {
            const displayVolume = formatVolume(plan.volume);

            if (!volumesMap[displayVolume]) {
                volumesMap[displayVolume] = [];
            }
            volumesMap[displayVolume].push(plan);
        });

        const volumes = Object.keys(volumesMap).sort((a, b) => {
            const aBytes = a.includes('GB') ? parseFloat(a) * 1024 : parseFloat(a);
            const bBytes = b.includes('GB') ? parseFloat(b) * 1024 : parseFloat(b);
            return aBytes - bBytes;
        });

        return volumes.map(volume => {
            const volumePlans = volumesMap[volume];
            const prices = volumePlans.map(p => p.price / 10000);
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            const priceRange = minPrice === maxPrice
                ? `$${minPrice.toFixed(2)}`
                : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;

            const representativePlan = volumePlans[0];

            return {
                volume: volume,
                priceRange: priceRange,
                name: representativePlan.name || representativePlan.packageCode,
                plans: volumePlans // Keep reference to original plans
            };
        });
    }

    /**
     * Prepares detailed package data for rendering
     * @param {Array<Object>} plans - Array of plan objects
     * @returns {Array<Object>} Array of formatted package details
     */
    preparePackageDetailsData(plans) {
        return plans.map(plan => {
            const price = (plan.price / 10000).toFixed(2);
            const displayVolume = formatVolume(plan.volume);

            let operatorListDisplay = '';
            if (plan.operatorList && Array.isArray(plan.operatorList)) {
                operatorListDisplay = plan.operatorList.join(', ');
            } else if (plan.operatorList) {
                operatorListDisplay = plan.operatorList;
            }

            let supportTopUpTypeDisplay = null;
            if (plan.supportTopUpType === 1) {
                supportTopUpTypeDisplay = i18n.t('no');
            } else if (plan.supportTopUpType === 2) {
                supportTopUpTypeDisplay = i18n.t('yes');
            }

            let activeTypeDisplay = null;
            if (plan.activeType === 1) {
                activeTypeDisplay = i18n.t('firstInstallation');
            } else if (plan.activeType === 2) {
                activeTypeDisplay = i18n.t('firstNetworkConnection');
            }

            let dataTypeDisplay = null;
            if (plan.dataType === 1) {
                dataTypeDisplay = i18n.t('dataInTotal');
            } else if (plan.dataType === 2) {
                dataTypeDisplay = i18n.t('dailyLimitSpeedReduced');
            } else if (plan.dataType === 3) {
                dataTypeDisplay = i18n.t('dailyLimitServiceCutoff');
            } else if (plan.dataType === 4) {
                dataTypeDisplay = i18n.t('dailyUnlimited');
            }

            const locationCode = plan.locationCode || plan.location;
            const locationName = this.getLocationName(locationCode) || locationCode;
            let countriesList = [];
            if (plan.locationNetworkList && Array.isArray(plan.locationNetworkList)) {
                countriesList = plan.locationNetworkList.map(loc => {
                    const code = loc.locationCode || loc.code || loc;
                    const name = this.getLocationName(code) || code;
                    return { code, name, flagIcon: this.getFlagIcon(code) };
                });
            }

            const charge = plan.charge ? (plan.charge / 10000).toFixed(2) : 0;

            return {
                name: plan.name || plan.packageCode,
                packageCode: plan.packageCode,
                price: price,
                charge: charge,
                displayVolume: displayVolume,
                duration: plan.duration,
                durationUnit: plan.durationUnit?.toLowerCase() || i18n.t('days'),
                hasSpeed: !!plan.speed,
                speed: plan.speed || '',
                smsStatus: plan.smsStatus ? i18n.t('included') : i18n.t('notIncluded'),
                dataType: dataTypeDisplay,
                activeType: activeTypeDisplay,
                operatorList: operatorListDisplay || null,
                supportTopUpType: supportTopUpTypeDisplay,
                locationCode: locationCode,
                locationName: locationName,
                hasCountries: countriesList.length > 0,
                countries: countriesList
            };
        });
    }
}

export default EsimStore;

